/**
 * 新闻舱 × 官网联动的展示口径(1009 一期,纯函数)。状态人话 / 徽标文案 / 改稿同步提示 / 可发体裁 / 排期时间。
 * 可发体裁与后端 ReporterSitePublishGate.GENRES 同源:新闻 / 品牌 / 活动 / 产品;小红书、问答不开放。
 */
import type { ReporterGenre } from "../api/reporterService";
import type {
  SiteOption,
  SitePublication,
  SitePublicationStatus,
} from "../api/reporterSiteService";

export const SITE_PUBLISHABLE_GENRES: ReadonlySet<ReporterGenre> = new Set([
  "COMPANY_NEWS",
  "BRAND_PROFILE",
  "EVENT_RELEASE",
  "PRODUCT_COPY",
]);

export const SITE_GENRE_BLOCKED_TEXT = "小红书笔记、问答文暂不支持发到官网";

export function canPublishGenre(genre: string | null | undefined): boolean {
  return !!genre && SITE_PUBLISHABLE_GENRES.has(genre as ReporterGenre);
}

export const SITE_STATUS_LABEL: Record<SitePublicationStatus, string> = {
  SCHEDULED: "已排期",
  PUBLISHING: "推送中",
  PUBLISHED: "已发布",
  FAILED: "发布失败",
  CANCELED: "已取消",
  UNPUBLISHED: "已下线",
};

export type SiteTone = "success" | "warning" | "destructive" | "muted" | "primary";

export const SITE_STATUS_TONE: Record<SitePublicationStatus, SiteTone> = {
  SCHEDULED: "primary",
  PUBLISHING: "primary",
  PUBLISHED: "success",
  FAILED: "destructive",
  CANCELED: "muted",
  UNPUBLISHED: "muted",
};

/** `2026-10-10T09:00:00` → `10-10 09:00`(字面切分,不过时区换算)。 */
export function shortTime(t: string | null | undefined): string {
  if (!t || t.length < 16) return t ?? "";
  return `${t.slice(5, 10)} ${t.slice(11, 16)}`;
}

/**
 * 站点地址 → 显示用域名(1009 D:同名站靠它分清)。宿主默认预览地址(`https://宿主/?siteId=xx`)各站同一个 host,
 * 这时带上 siteId;解析不了返回 null。
 */
export function siteHost(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    const sid = u.searchParams.get("siteId");
    return sid ? `${u.host}?siteId=${sid}` : u.host;
  } catch {
    return null;
  }
}

/** 「站名 · 域名」;没地址就只给站名;站点已删给「站点已删除」。 */
export function siteLabel(name: string | null | undefined, url: string | null | undefined): string {
  const host = siteHost(url);
  const n = name?.trim() || (host ? "" : "站点已删除");
  return [n, host].filter(Boolean).join(" · ");
}

/** 站点有没有动态新闻列表:true = 自动展示,false = 只能经链接访问,null = 宿主暂时查不到。 */
export function siteListText(articleListSupported: boolean | null | undefined): string {
  if (articleListSupported === true) return "新闻列表自动展示";
  if (articleListSupported === false) return "仅链接可见";
  return "新闻列表展示待确认";
}

/** 站点下拉选项文案:「站名 · 域名(新闻列表自动展示 / 仅链接可见)」。 */
export function siteOptionText(o: SiteOption): string {
  return `${siteLabel(o.name, o.siteUrl)}(${siteListText(o.articleListSupported)})`;
}

/** 徽标一行:「已排期 · 10-10 09:00」「已发布」「发布失败」。 */
export function badgeText(p: SitePublication): string {
  const label = SITE_STATUS_LABEL[p.status] ?? p.status;
  if (p.status === "SCHEDULED") {
    const at = p.nextAttemptAt ?? p.scheduledAt;
    return `${label} · ${shortTime(at)}${p.attempts > 0 ? `(第 ${p.attempts + 1} 次尝试)` : ""}`;
  }
  return label;
}

/** 改稿后提示:「官网是第 N 版,要同步吗」;不需要提示 → null。 */
export function outdatedPrompt(p: SitePublication): string | null {
  if (!p.outdated || p.publishedVersion == null) return null;
  return `官网上是第 ${p.publishedVersion} 版,文章已改到第 ${p.currentVersion} 版,要同步更新吗?`;
}

/** 多站点汇总成一枚徽标:优先级 失败 > 推送中 > 已排期 > 已发布 > 已下线 > 已取消。 */
const PRIORITY: SitePublicationStatus[] = [
  "FAILED",
  "PUBLISHING",
  "SCHEDULED",
  "PUBLISHED",
  "UNPUBLISHED",
  "CANCELED",
];

export function summarize(items: SitePublication[]): SitePublication | null {
  for (const s of PRIORITY) {
    const hit = items.find((i) => i.status === s);
    if (hit) return hit;
  }
  return null;
}

/** datetime-local 输入的最小值(现在 + 5 分钟,本地时间 `YYYY-MM-DDTHH:mm`)。 */
export function minScheduleInput(now: Date = new Date()): string {
  const d = new Date(now.getTime() + 5 * 60_000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** 按日期分组(`YYYY-MM-DD`),组内按时间升序;没有时间的归「未排期」。 */
export function groupByDate<T>(
  rows: T[],
  at: (r: T) => string | null | undefined
): { date: string; rows: T[] }[] {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const t = at(r);
    const key = t && t.length >= 10 ? t.slice(0, 10) : "未排期";
    (map.get(key) ?? map.set(key, []).get(key)!).push(r);
  }
  return [...map.entries()]
    .sort(([a], [b]) => (a === "未排期" ? 1 : b === "未排期" ? -1 : a.localeCompare(b)))
    .map(([date, rs]) => ({
      date,
      rows: [...rs].sort((x, y) => (at(x) ?? "").localeCompare(at(y) ?? "")),
    }));
}

/**
 * 「从建站舱新闻栏 → 新写一篇」带过来的目标站点:创建页记下,预览页发布弹窗预选。只存本标签页
 * (sessionStorage),读写失败静默(隐私模式等)。
 */
const SITE_TARGET_KEY = "reporter.siteTarget";

export function rememberSiteTarget(siteId: string | null | undefined): void {
  if (!siteId) return;
  try {
    sessionStorage.setItem(SITE_TARGET_KEY, siteId);
  } catch {
    /* 存不了就算了:弹窗里再选一次 */
  }
}

export function recallSiteTarget(): string | null {
  try {
    return sessionStorage.getItem(SITE_TARGET_KEY);
  } catch {
    return null;
  }
}

/** react-query 键(弹窗 / 面板 / 作品列表共用)。 */
export const SITE_OPTIONS_KEY = ["reporter-site-options"] as const;

export const articlePublicationsKey = (articleId: number | string) =>
  ["reporter-site-publications", String(articleId)] as const;
